"use client";
import { useState, useEffect, type CSSProperties } from 'react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Icon } from '@/components/ui/Icon';
import { Button } from './Button';
import { useTaxonomyStore } from "@/stores/taxonomy-store";
import { useLocale } from "next-intl";
import { FEATURE_TEXT_MAX_LENGTH } from "@/lib/constants";
import { getTaxonomyIconUrl } from "@/lib/utils/amenity-icons";
import { RequiredMark } from "./RequiredMark";

interface FeatureCardProps {
  index: number;
  label: string;
  titlePlaceholder: string;
  descPlaceholder: string;
  iconValue: string;
  titleValue: string;
  descValue: string;
  onUpdate: (data: { icon?: string; title?: string; desc?: string }) => void;
}

/** CSS mask so this inherits `currentColor` — the source SVGs hardcode a stroke color, so a plain <img> wouldn't follow hover/selected text-color classes. */
export function FeatureIcon({ iconKey, size, className }: { iconKey: string; size: number; className?: string }) {
  const url = getTaxonomyIconUrl(iconKey);
  const maskStyle: CSSProperties = {
    width: size,
    height: size,
    backgroundColor: "currentColor",
    maskImage: `url(${url})`,
    WebkitMaskImage: `url(${url})`,
    maskRepeat: "no-repeat",
    WebkitMaskRepeat: "no-repeat",
    maskPosition: "center",
    WebkitMaskPosition: "center",
    maskSize: "contain",
    WebkitMaskSize: "contain",
  };
  return <span aria-hidden="true" className={className} style={maskStyle} />;
}

export function FeatureInputCard({
  index,
  label,
  titlePlaceholder,
  descPlaceholder,
  iconValue,
  titleValue,
  descValue,
  onUpdate,
}: FeatureCardProps) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const locale = useLocale();
  const { groups, fetchGroups } = useTaxonomyStore();

  useEffect(() => {
    fetchGroups(['feature_icons'], locale);
  }, [fetchGroups, locale]);

  const taxonomyIcons = groups[`${locale}:feature_icons`] || [];

  return (
    <div className="border border-secondary/20 rounded-xl p-4 mb-4">
      <h3 className="flex items-center text-xs font-bold uppercase tracking-widest text-secondary mb-4">
        <span className="w-6 h-6 rounded-full bg-primary-50 text-primary-500 flex items-center justify-center font-sans text-[11px] mr-2">
          {index}
        </span>
        <span>{label}</span>
        <span className="font-sans ml-1">{index}</span>
        <RequiredMark className="ml-1" />
      </h3>

      <div className="flex gap-3 mb-1 relative">
        <Button
          className="rounded-lg border border-secondary/20 shrink-0 bg-bg flex items-center justify-center hover:bg-background-200/60"
          onClick={() => setIsPopupOpen(!isPopupOpen)}
        >
          <FeatureIcon iconKey={iconValue} className="text-secondary" size={24} />
        </Button>
        <Input
          placeholder={titlePlaceholder}
          value={titleValue}
          onChange={(e) => onUpdate({ title: e.target.value.slice(0, FEATURE_TEXT_MAX_LENGTH) })}
          maxLength={FEATURE_TEXT_MAX_LENGTH}
          className="flex-1 bg-bg"
        />
        {isPopupOpen && (
          <div className="absolute left-0 top-[52px] z-50 w-[300px] bg-white rounded-[16px] shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-foreground-100 p-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-foreground-400 mb-4">
                Pick icon
              </p>
              <button
                type="button"
                onClick={() => setIsPopupOpen(false)}
                className="text-foreground-400 hover:text-foreground-700 transition-colors"
              >
                <Icon name="close" size={16} />
              </button>
            </div>
            {/* Sized to exactly 4 full rows (5 cols x ~47.2px each + gaps in this 300px popover) so a short list never scrolls — only a real 5th+ row does. */}
            <div className="grid grid-cols-5 gap-2 max-h-[216px] overflow-y-auto">
              {taxonomyIcons.map((icon, i) => {
                const isSelected = iconValue === icon.key;
                return (
                  <button
                    key={i}
                    type="button"
                    title={icon.label}
                    onClick={() => {
                      onUpdate({ icon: icon.key }); 
                      setIsPopupOpen(false);
                    }}
                    className={`
                      w-full aspect-square flex items-center justify-center rounded-xl transition-all duration-200
                      ${isSelected
                        ? "bg-primary-50 text-primary-500" 
                        : "text-foreground-500 hover:bg-background-50 hover:text-foreground-700"
                      }
                    `}
                  >
                    <FeatureIcon iconKey={icon.key} size={20} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end mb-3">
        <span className="text-xs text-foreground-400">
          {titleValue.length}/{FEATURE_TEXT_MAX_LENGTH}
        </span>
      </div>

      <Textarea
        placeholder={descPlaceholder}
        value={descValue}
        onChange={(e) => onUpdate({ desc: e.target.value.slice(0, FEATURE_TEXT_MAX_LENGTH) })}
        maxLength={FEATURE_TEXT_MAX_LENGTH}
        className="bg-bg"
      />
      <div className="flex justify-end mt-2">
        <span className="text-xs text-foreground-400">
          {descValue.length}/{FEATURE_TEXT_MAX_LENGTH}
        </span>
      </div>
    </div>
  );
}